import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { 
  TrendingUp, 
  TrendingDown, 
  Users, 
  Briefcase, 
  Clock, 
  AlertTriangle,
  Building2,
  UserCheck,
  Calendar,
  Activity
} from 'lucide-react';
import { AdminSystemStatsDto } from '../../types';

interface AdminStatsCardsProps {
  stats: AdminSystemStatsDto;
  isLoading?: boolean;
}

const AdminStatsCards: React.FC<AdminStatsCardsProps> = ({ stats, isLoading }) => {
  if (isLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {Array.from({ length: 8 }).map((_, index) => (
          <Card key={index} className="animate-pulse">
            <CardHeader className="pb-2">
              <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4"></div>
            </CardHeader>
            <CardContent>
              <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/2 mb-2"></div>
              <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-full"></div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  const formatNumber = (num: number) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toString();
  };

  const formatPercentage = (num: number) => {
    const sign = num >= 0 ? '+' : '';
    return `${sign}${num.toFixed(1)}%`;
  };

  const getTrendIcon = (value: number) => {
    return value >= 0 ? (
      <TrendingUp className="h-4 w-4 text-green-500" />
    ) : (
      <TrendingDown className="h-4 w-4 text-red-500" />
    );
  };

  const getTrendColor = (value: number) => {
    return value >= 0 ? 'text-green-600' : 'text-red-600';
  };

  const cards = [
    {
      title: 'Total Work Orders',
      value: formatNumber(stats.totalWorkOrders),
      icon: <Briefcase className="h-5 w-5" />,
      trend: stats.monthlyGrowthPercentage,
      trendLabel: 'vs last month',
      color: 'text-accent-color'
    },
    {
      title: 'Active Users',
      value: formatNumber(stats.activeUsers),
      icon: <Users className="h-5 w-5" />,
      trend: stats.userGrowthPercentage,
      trendLabel: 'vs last month',
      color: 'text-green-600'
    },
    {
      title: 'Open Work Orders',
      value: formatNumber(stats.openWorkOrders),
      icon: <Activity className="h-5 w-5" />,
      trend: null,
      trendLabel: null,
      color: 'text-orange-600'
    },
    {
      title: 'Overdue Work Orders',
      value: formatNumber(stats.overdueWorkOrders),
      icon: <AlertTriangle className="h-5 w-5" />,
      trend: stats.overduePercentage,
      trendLabel: '% of total',
      color: 'text-red-600'
    },
    {
      title: 'Avg Resolution Time',
      value: `${stats.averageResolutionTimeHours.toFixed(1)}h`,
      icon: <Clock className="h-5 w-5" />,
      trend: null,
      trendLabel: null,
      color: 'text-purple-600'
    },
    {
      title: 'Total Companies',
      value: formatNumber(stats.totalCompanies),
      icon: <Building2 className="h-5 w-5" />,
      trend: null,
      trendLabel: `${stats.activeCompanies} active`,
      color: 'text-indigo-600'
    },
    {
      title: 'Total Vendors',
      value: formatNumber(stats.totalVendors),
      icon: <UserCheck className="h-5 w-5" />,
      trend: null,
      trendLabel: `${stats.activeVendors} active`,
      color: 'text-teal-600'
    },
    {
      title: 'This Month',
      value: formatNumber(stats.workOrdersThisMonth),
      icon: <Calendar className="h-5 w-5" />,
      trend: stats.monthlyGrowthPercentage,
      trendLabel: 'vs last month',
      color: 'text-cyan-600'
    }
  ];

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
      {cards.map((card, index) => (
        <Card key={index} className="hover:shadow-lg transition-shadow">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-gray-600 dark:text-gray-400">
              {card.title}
            </CardTitle>
            <div className={card.color}>
              {card.icon}
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold mb-1 text-gray-900 dark:text-gray-100">{card.value}</div>
            {card.trend !== null && (
              <div className="flex items-center space-x-1 text-sm">
                {getTrendIcon(card.trend)}
                <span className={getTrendColor(card.trend)}>
                  {formatPercentage(card.trend)}
                </span>
                <span className="text-gray-500 dark:text-gray-400">{card.trendLabel}</span>
              </div>
            )}
            {card.trend === null && card.trendLabel && (
              <div className="text-sm text-gray-500 dark:text-gray-400">
                {card.trendLabel}
              </div>
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

export default AdminStatsCards;
